import Web3 from 'web3'
import BigNumber from 'bignumber.js'
import { AbiItem } from 'web3-utils'
import { ethers } from 'ethers'
import { provider as ProviderType } from 'web3-core'
import { useWallet } from '@binance-chain/bsc-use-wallet'
import { useState, useEffect, useCallback } from 'react'
import bridgeAbi from 'config/abi/bridge.json'
import erc20Abi from 'config/abi/erc20.json'
import { Config, getTokenAddress, getTokenDecimals, getTokenList, approve, depositTokens, claim, getAuth, getWeb3, getTokenBalancesOfAllChains, getBridgeAddress } from 'utils/bridgeUtils'
import useRefresh from 'hooks/useRefresh'

declare const window: any
const BRIDGEABI = (bridgeAbi as unknown) as AbiItem
const ERC20ABI = (erc20Abi as unknown) as AbiItem

export const useBalance = (account) => {
    const [balance, setBalance] = useState<any>({})
    const { fastRefresh } = useRefresh()
  
    useEffect(() => {
        const fetch = async () => {
            if (account) {
                try {
                    const result = await getTokenBalancesOfAllChains(account);
                    setBalance(result);
                    return;
                } catch (e) {
                  console.error(e)
                }
            }
            setBalance({})
        }

        fetch();
    }, [account, fastRefresh])
  
    return balance
}


// eslint-disable-next-line
window.__temp = Web3;

export const useApprove = (chainId, account, tokenSymbol) => {
    const handleApprove = useCallback(async () => {
      try {
        const web3 = new Web3(window.ethereum);
        const tokenAddress = getTokenAddress(chainId, tokenSymbol)
        const contract = new web3.eth.Contract(ERC20ABI, tokenAddress); 
        const bridgeAddress = getBridgeAddress(chainId);
        if (contract && account && bridgeAddress) {
            let th = "";
            let altHandle = null;
            let receipt = null;
            const approveTask:any = approve(contract, bridgeAddress, account);
            approveTask.on('transactionHash', (hash) => {
              th = hash;
            }).then((r)=>{
              receipt = r;
            });
            const altTask  = new Promise((resolve,reject) => {
              let count = 0;
              altHandle = setInterval(()=>{
                if (th) {
                  web3.eth.getTransactionReceipt(th).then((re)=>{
                    if (re) {
                      resolve("Alt");
                      receipt = re;
                      clearInterval(altHandle);
                      altHandle= null;
                    } else if (count++ >= 12) {
                      clearInterval(altHandle);
                      altHandle= null;
                      reject(new Error("Too many trials to get the transaction receipt."));
                    }
                  })
                }
              }, 5000);
            });
            await Promise.race([
                approveTask,
                altTask
            ]);
            if (altHandle) clearInterval(altHandle);
            return receipt?.status ? th : false;
        }
      } catch (e) {
        console.error(e)
      }
      return false;
    }, [account, chainId, tokenSymbol])
  
    return handleApprove;
}

export const useTokenList = (chainId1, chainId2) => {
    const [list, setList] = useState([])
  
    useEffect(() => {
        setList(getTokenList(chainId1, chainId2));
    }, [chainId1, chainId2])
  
    return list
}

export const useTokenAddress = (chainId, tokenSymbol) => {
  const [address, setAddress] = useState("")

  useEffect(() => {
    setAddress(getTokenAddress(chainId, tokenSymbol));
  }, [chainId, tokenSymbol])

  return address
}

export const useDepositTokens = (fromChainId, toChainId, account, tokenSymbol) => {
    const handle = useCallback(async (amount: BigNumber) => {
      try {
        const web3 = new Web3(window.ethereum);
        const bridgeAddress = Config.chains[fromChainId]?.bridge
        const contract = new web3.eth.Contract(BRIDGEABI, bridgeAddress); 
        const tokenAddress = getTokenAddress(fromChainId, tokenSymbol);
        if (contract && tokenAddress && account && toChainId) {
          let th = "";
          let altHandle = null;
          let receipt = null;
          const depositTask:any = depositTokens(
            contract, tokenAddress, 
            amount.times(new BigNumber(10).pow(getTokenDecimals(tokenSymbol)||0)).toFixed(0), 
            toChainId, account
          );
          depositTask.on('transactionHash', (hash) => {
            th = hash;
          }).then((r)=>{
            receipt = r;
          });
          const altTask  = new Promise((resolve,reject) => {
            let count = 0;
            altHandle = setInterval(()=>{
              if (th) {
                web3.eth.getTransactionReceipt(th).then((re)=>{
                  if (re) {
                    resolve("Alt");
                    receipt = re;
                    clearInterval(altHandle);
                    altHandle= null;
                  } else if (count++ >= 12) {
                    clearInterval(altHandle);
                    altHandle= null;
                    reject(new Error("Too many trials to get the transaction receipt."));
                  }
                })
              }
            }, 5000);
          });
          await Promise.race([
            depositTask,
            altTask
          ]);
          if (altHandle) clearInterval(altHandle);
          return receipt;
        }
      } catch (e) {
        console.error(e)
      }
      return false;
    }, [account, tokenSymbol, fromChainId, toChainId])
  
    return handle;
}

export const useClaim = (fromChainId, toChainId, txId, account) => {
    const handle = useCallback(async () => {
        try {
          const web3 = new Web3(window.ethereum);
          const bridgeAddress = Config.chains[toChainId]?.bridge
          const contract = new web3.eth.Contract(BRIDGEABI, bridgeAddress); 
          if (contract && fromChainId && txId && account ) {
            const data = await getAuth(fromChainId, txId);
            if (data) {
                if (!data.isSuccess) return data;
                if (Number(toChainId)!==Number(data.chainId)) return {"isSuccess": false, "message": "Wrong destination chain"};
                const isTxProcessed = await contract.methods.isTxProcessed(fromChainId, txId).call();
                if (isTxProcessed) return {"isSuccess": false, "message": "Already processed"};

                let th = "";
                let altHandle = null;
                let receipt = null;
                const claimTask:any = claim(contract, data.token, txId, data.to, data.value, fromChainId, [data.signature], account);
                claimTask.on('transactionHash', (hash) => {
                  th = hash;
                }).then((r)=>{
                  receipt = r;
                });
                const altTask  = new Promise((resolve,reject) => {
                  let count = 0;
                  altHandle = setInterval(()=>{
                    if (th) {
                      web3.eth.getTransactionReceipt(th).then((re)=>{
                        if (re) {
                          resolve("Alt");
                          receipt = re;
                          clearInterval(altHandle);
                          altHandle= null;
                        } else if (count++ >= 12) {
                          clearInterval(altHandle);
                          altHandle= null;
                          reject(new Error("Too many trials to get the transaction receipt."));
                        }
                      })
                    }
                  }, 5000);
                });
                await Promise.race([
                  claimTask,
                  altTask
                ]);
                if (altHandle) clearInterval(altHandle);
                return {"isSuccess": receipt?.status, "message": ""};
            }            
          }
        } catch (e) {
            console.log(e)
        }
        return {"isSuccess": false, "message": "Unknown error"} ;
      }, [fromChainId, toChainId, txId, account])
    
      return handle;
}
